import React, {
  Component,
  forwardRef,
  memo,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import deepEqual from './utils/deepEqual';
import getIn from './utils/getIn';
import keys from './utils/keys';
import mapValues from 'lodash/mapValues';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as importedActions from './utils/actions';
import { createValidationRules } from './utils/createValidationRules';
import { getFragmentFields } from 'graphql/schema/getQueryFields';
import processValidation from './utils/processValidation';
import handleSubmit from './utils/handleSubmit';
import styled from 'styled-components';
import { getModuleName } from 'utils/getModuleName';
import setDocumentTitle from 'utils/SetDocumentTitle';
import ProcessingModal from '../modals/ProcessingModal';
import { checkOperationEdit } from 'utils/OperationPermissions';
import checkReadOnly from './utils/checkReadOnly';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import AppContext from '../Context/AppContext';
import ModalContext from '../Context/ModalContext';
import { usePristineEntity } from './Hooks/pristineEntityHook';
import { generatePath, useHistory, useLocation, useRouteMatch } from 'react-router';
import { useHeaderAlerts } from './Hooks/useHeaderAlerts';
import { useAlertsAndJobs } from './Hooks/useAlertsAndJobs';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { useConnectedFormBackNavigation } from './useConnectedFormBackNavigation';
import MergeConflicts from '../MergeConflicts/MergeConflicts';
import AppConfirmationPrompt from '../../views/system/AppConfirmationPrompt';
import PostSaveModals from './PostSaveModals';
import { useManagedSubmit } from './useManagedSubmit';
import { useConcurrencyReload } from './UseConcurrencyReload';
import { useProcessFormData } from './UseProcessFormData';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import ViewContext, { ViewContextProvider } from '../Context/ViewContext';

const FormWrapper = styled.div`
  height: 100%;
`;

const { blur, change, focus, ...formActions } = importedActions;

let propsToNotUpdateFor = [
  ...Object.keys(importedActions),
  'loadingData',
  'initialValues',
  'values',
  'registeredFields',
  'newform',
  'formData',
  'nominalConfig',
  'company',
];

function processConfig(initialConfig) {
  const { name, form, entity, ...rest } = initialConfig;

  return {
    touchOnBlur: true,
    touchOnChange: false,
    getFormState: (state) => getIn(state, 'newform'),
    entity: entity,
    name: name || getIn(entity, 'formTitle'), //Remove the requirement to pass name which is usually == formTitle anyway.
    form: form || getIn(entity, 'type'), //Remove the requirement to set form which is usually == type anyway.
    initialValues: {},
    ...rest,
  };
}

export default function createForm(initialConfig) {
  const config = processConfig(initialConfig);

  return (WrappedComponent) => {
    class Form extends Component {
      constructor(props) {
        super(props);
        // TODO - This is a temporary exceptional circumstance until a more targeted solution is devised

        if (props.forceUpdate) {
          propsToNotUpdateFor = propsToNotUpdateFor.filter((e) => {
            return e !== 'newform' && e !== 'formData';
          });
        }

        this.cancel = this.cancel.bind(this);
        this.submit = this.submit.bind(this);
        this.register = this.register.bind(this);
        this.validate = this.validate.bind(this);
        this.getValues = this.getValues.bind(this);
        this.unregister = this.unregister.bind(this);
        this.getConnectedForm = this.getConnectedForm.bind(this);
        this.formMounted = false;
        this.messageDefinition = {};
        this.fieldValidationConfig = {};
        this.validationRules = () => {};

        this.state = {};
      }

      getConnectedForm() {
        const { children, ...rest } = this.props;

        return {
          ...rest,
          forceSubmit: this.submit,
          getFormState: (state) => getIn(this.props.getFormState(state), this.props.form),
          getValues: this.getValues,
          register: this.register,
          unregister: this.unregister,
          validate: this.validate,
          cancel: this.cancel,
        };
      }

      componentDidMount() {
        this.formMounted = true;
      }

      componentDidUpdate(prevProps, prevState, snapshot) {
        this.initIfNeeded(prevProps, this.props);

        //loadFields is an override to let a form with no entity load default data from formConfig.fields.
        //Note: Moved here from 'loadDataIfNeeded' since the logic from that function has been lifted up to HookForm.
        if (this.props.formConfig?.loadFields && !this.props.initialized) {
          this.props.initialize(this.props.formConfig.fields, this.props.fieldValidation);
        }

        this.clearSubmitPromiseIfNeeded(prevProps, this.props);
        this.submitIfNeeded(prevProps, this.props);
        if (this.props.onChange) {
          if (!deepEqual(this.props.values, prevProps.values)) {
            this.props.onChange(this.props.values, this.props.dispatch, this.props);
          }
        }
      }

      componentWillUnmount() {
        const { destroy } = this.props;
        this.destroyed = true;
        destroy();
      }

      getValues() {
        return this.props.values;
      }

      getFieldList() {
        const { registeredFields } = this.props;
        const list = [];
        if (!registeredFields) {
          return list;
        }
        const keySeq = keys(registeredFields);
        return keySeq.reduce((acc, key) => {
          acc.push(key);
          return acc;
        }, list);
      }

      unregister(dataid) {
        if (!this.destroyed) {
          this.props.unregisterField(dataid);
        }
      }

      register(dataid, type, tabId) {
        this.props.registerField(dataid, type, tabId);
      }

      validate(dataid, value) {
        const { dispatch, startValidation, stopValidation, values } = this.props;
        return processValidation(
          () => this.validationRules(values, dispatch, this.props),
          startValidation,
          stopValidation,
          dataid
        );
      }

      /*
       * TODO - Review - useManagedSubmit may be handling this well enough by itself.
       * Is this still relevant????
       * */
      clearSubmitPromiseIfNeeded(prevProps, nextProps) {
        const { submitting } = prevProps;
        if (this.props.submitPromise.current && submitting && !nextProps.submitting) {
          this.props.submitPromise.current = null;
        }
      }

      submitIfNeeded(prevProps, nextProps) {
        const { clearSubmit, triggerSubmit } = prevProps;
        if (!triggerSubmit && nextProps.triggerSubmit) {
          clearSubmit();
          this.submit();
        }
      }

      initIfNeeded(prevProps, nextProps) {
        if (!prevProps.initialized && nextProps.initialized) {
          const fieldMeta = nextProps.fieldMeta;
          const fieldList = Object.keys(fieldMeta);
          fieldList.forEach((field) => {
            const meta = fieldMeta[field];
            const fieldType = getIn(meta, 'type');
            const value = getIn(meta, 'validationRules');
            const minValue = getIn(value, 'minValue');
            const maxValue = getIn(value, 'maxValue');
            const pattern = getIn(value, 'pattern');
            const patternMessage = getIn(value, 'patternMessage');
            let mandatory = getIn(value, 'mandatory');
            let validDate = null;
            if (mandatory && fieldType === 'LocalDate') {
              validDate = true;
              mandatory = false;
            }
            const numeric = fieldType === 'BigDecimal';
            if (validDate || mandatory || minValue || maxValue || pattern || numeric) {
              const _mandatory = mandatory && { mandatory };
              const _validDate = validDate && { validDate };
              const _minValue = minValue && { minValue };
              const _maxValue = maxValue && { maxValue };
              const _pattern = pattern && { pattern };
              const _patternMessage = patternMessage && { patternMessage };
              const _numeric = numeric && { numeric };

              this.fieldValidationConfig[field] = {
                ..._mandatory,
                ..._validDate,
                ..._minValue,
                ..._maxValue,
                ..._pattern,
                ..._patternMessage,
                ..._numeric,
              };
            }
          });
          this.validationRules = createValidationRules(this.fieldValidationConfig, nextProps.registeredFields);
        }
      }

      cancel(event) {
        if (this.props.onClose) return this.props.onClose(event);
        this.props.navigateBack();
      }

      submit(submitOptions = {}) {
        this.validate();
        const props = this.props;
        const { submitValidation, suppressSuccessMessage } = this.props;
        if (submitValidation && submitValidation(props)) return;
        this.props.setSubmitInProgress(true);
        return (
          !this.props.submitPromise.current &&
          this.props.listenToSubmit(
            handleSubmit(
              {
                ...this.props,
                ...submitOptions,
              },
              this.getFieldList(),
              'saved',
              { suppressSuccessMessage }
            )
          )
        );
      }

      render() {
        const {
          anyTouched,
          blur,
          change,
          children,
          customValidationErrors,
          destroy,
          dirty,
          dispatch,
          error,
          focus,
          form,
          fieldErrors,
          getFormState,
          initialize,
          initialized,
          initialValues,
          invalid,
          pristine,
          registeredFields,
          registerField,
          reset,
          setSubmitFailed,
          setSubmitSucceeded,
          shouldValidate,
          startSubmit,
          stopSubmit,
          submitting,
          startValidation,
          stopValidation,
          submitFailed,
          submitSucceeded,
          touch,
          touchOnBlur,
          touchOnChange,
          unregisterField,
          untouch,
          valid,
          validating,
          values,
          validationErrors,
          ...rest
        } = this.props;
        const connectedFormProps = {
          anyTouched,
          blur,
          cancel: this.cancel,
          change,
          destroy,
          dirty,
          dispatch,
          error,
          form,
          initialize,
          initialized,
          initialValues,
          invalid,
          pristine,
          reset,
          submitting,
          submitFailed,
          submitSucceeded,
          touch,
          untouch,
          valid,
          validating,
          validationErrors,
          customValidationErrors,
          values,
        };
        const propsToPass = {
          ...connectedFormProps,
          ...rest,
        };

        let busyModal;
        if (this.props && this.props.busy) {
          busyModal = <ProcessingModal key={'Processing'} />;
        }

        return (
          <FormWrapper id={this.props?.entity?.type}>
            <ConnectedFormContext.Provider value={this.getConnectedForm()}>
              <WrappedComponent {...propsToPass} />
              {busyModal}
              <PostSaveModals />
              {children}
              <AppConfirmationPrompt customPromptHandlers={this.props.customPromptHandlers} />
            </ConnectedFormContext.Provider>
          </FormWrapper>
        );
      }
    }

    Form.propTypes = {
      form: PropTypes.string.isRequired,
      initialValues: PropTypes.object,
      getFormState: PropTypes.func,
      onSubmitSuccess: PropTypes.func,
      propNameSpace: PropTypes.string,
      validate: PropTypes.func,
      touchOnBlur: PropTypes.bool,
      touchOnChange: PropTypes.bool,
      triggerSubmit: PropTypes.bool,
      pristine: PropTypes.bool,
      valid: PropTypes.bool,
      values: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), //Permitted array type for dispatch form.
      registeredFields: PropTypes.any,
    };

    //Hooks wrapper for slow migration of ConnectedForm.
    const HookForm = memo(
      forwardRef((props, ref) => {
        const {
          entity,
          location,
          match,
          defaultSaveOption = { value: 'EXIT' },
          defaultReturnUrl,
          modalClose,
          onClose,
          modal,
          customPristineDependency,
          postSaveFunction,
          form,
          touchOnBlur,
          touchOnChange,
        } = props;
        const params = match?.params;
        const dispatch = useDispatch();
        const history = useHistory();
        const fieldValidation = useMemo(
          () => ('Parameters' ? getFragmentFields(props.queryFragment) : props.formConfig.validation || {}),
          [props.queryFragment, entity.type, params?.id]
        );

        const formMounted = useRef(false);
        const submitOptions = useReducer((state, action) => {
          switch (action.type) {
            case SubmitOption.Exit:
              return { ...state, value: 'EXIT' };
            case SubmitOption.Continue:
              return { ...state, value: 'CONTINUE' };
            case SubmitOption.New:
              return { ...state, value: 'NEW' };
            default:
              throw new Error();
          }
        }, defaultSaveOption);
        const app = useContext(AppContext);
        const modalContext = useContext(ModalContext);
        const workspaceReloadRef = useRef([]); //Set of functions for reloading nested workspace data, as appropriate.
        const formContext = useContext(ConnectedFormContext);

        /**
         * If the instance query re-fetches (Such as due to a cache eviction) and the re-fetched data has an updated concurrency version,
         * fire off a concurrency reload automatically as we may be able to prevent manual conflict resolution from being required.
         *
         * The scenario where this runs is when you print via the DocumentCarousel - The cache for related entities is evicted because
         * it is out-of-date, which needs to trigger a concurrency reload on the form to avoid needing a manual merge on save.
         */
        useEffect(() => {
          //Only trigger if we already had a record loaded, the id has not changed (We haven't loaded another record) and the concurrency
          //version explicitly differs from that in values (Mutations will update values, whereas cache evictions will not).
          if (
            props.formData?.data?.concurrencyVersion &&
            connectedFormState.values?.concurrencyVersion &&
            props.formData?.data?.id === connectedFormState.values?.id &&
            props.formData?.data?.concurrencyVersion !== connectedFormState.values?.concurrencyVersion
          ) {
            processFormData(props.formData, { isConcurrencyReload: true });
          }
          //Explicitly looking for concurrencyVersion changes only.
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.formData?.data?.concurrencyVersion]);

        /**
         * Triggers a reload of form data.
         * Set 'processingRequired' to true to manually trigger reloading.
         * Otherwise, it will refresh for id/uuid changes only (And should be set to false if this is the desired behaviour).
         */
        const reloadData = props.formData
          ? (processingRequired, { getDefault = false, isConcurrencyReload = false } = {}) => {
              workspaceReloadRef.current.forEach((workspaceReload) => workspaceReload?.());

              if (
                !getDefault &&
                props.formData.isDefault &&
                connectedFormState.values?.id &&
                !formContext?.values &&
                !modalClose &&
                !isConcurrencyReload &&
                match.path?.includes('/New')
              ) {
                // If formContext has values, then we must be in a "Form within a Form" - e.g. Raising a PO from an SO
                // If this is the case, don't reload the form by navigating away
                const path = match.path?.replace('/New', `/${connectedFormState.values?.id}`);
                history.replace(path);
              }

              return new Promise((resolve, reject) => {
                const fetchPromise = isConcurrencyReload
                  ? props.formData.fetchInstance({ variables: { id: connectedFormState.values.id } })
                  : props.formData.refetch();

                fetchPromise.then((newFormData) => {
                  if (!newFormData?.data || !formMounted) {
                    console.warn('Attempted to refetch but no result was received');
                    reject();
                    return;
                  }
                  if (processingRequired) {
                    resolve(
                      processFormData(
                        {
                          ...newFormData,
                          defaultRecord: newFormData.data?.defaultRecord,
                          data: newFormData.data?.data,
                        },
                        { isConcurrencyReload }
                      )
                    );
                  } else {
                    resolve();
                  }
                });
              });
            }
          : null;

        const connectedFormState = useSelector((store) => {
          const { form, getFormState, initialValues, noData } = props;
          const formState = getIn(getFormState(store) || {}, form) || {};
          const stateInitial = formState?.initial;
          const initialized = !!stateInitial || noData;
          const initial = initialValues || stateInitial || {};

          const {
            fieldErrors = {},
            meta: fieldMeta = {},
            fields = {},
            registeredFields,
            anyTouched = false,
            submitting = false,
            submitFailed = false,
            submitSucceeded = false,
            error,
            triggerSubmit,
            loadingData,
            validating = false,
            validationErrors,
            forceReadOnly,
            busy,
          } = formState;
          const values = formState.values || initial || {};

          //Read Only
          const locationReadOnly = !!location?.state?.readOnly; //Current route is read only (e.g. Modal Views).
          const editPermitted = checkOperationEdit({ ...props, app });
          const completeStatus = checkReadOnly(entity.readOnlyStatus, values, editPermitted);
          //Unless we've explicitly opted in to being able to edit via forceReadOnly, archived records should not be editable.
          const archivedReadOnly = forceReadOnly ?? values.archived;
          const readOnly =
            busy || props.readOnly || locationReadOnly || completeStatus || forceReadOnly || archivedReadOnly;

          return {
            anyTouched,
            autoFocus: store.app?.autoFocus,
            error,
            focusArea: store.app?.focusArea,
            initialized,
            loadingData,
            readOnly,
            registeredFields,
            submitting,
            submitFailed,
            submitSucceeded,
            fieldErrors,
            fieldMeta,
            fields,
            triggerSubmit,
            values,
            validating,
            validationErrors,
            busy,
          };
        });

        const concurrencyReloadData = useConcurrencyReload(
          reloadData,
          entity,
          connectedFormState.values,
          submitOptions
        );

        const { navigateBack, afterSubmit } = useConnectedFormBackNavigation({
          defaultReturnUrl,
          entity,
          location,
          modalClose,
          onClose,
          submitOptions,
          reloadData,
        });

        const { submitPromise, submitInProgress, setSubmitInProgress, submitFunctions, listenToSubmit } =
          useManagedSubmit({
            name: props.name,
            afterSubmit,
            modal,
            submitOptions,
            postSaveFunction,
          });

        const bindForm = (actionCreator) => actionCreator.bind(null, props.form);

        // Bind the first parameter on `props.form`
        const boundFormACs = mapValues(formActions, bindForm);
        const boundFocus = bindForm(focus);

        //Bind the blur/change functions with the form type, mutationId & touch props (Note - touch props may be disused) + bind them to the redux dispatch.
        const { dispatchBlur, dispatchChange } = useMemo(() => {
          const boundBlur = (field, value, mutationId) => blur(form, field, value, !!touchOnBlur, mutationId);
          const boundChange = (field, value, mutationId) => change(form, field, value, !!touchOnChange, mutationId);

          const { blur: dispatchBlur, change: dispatchChange } = bindActionCreators(
            { blur: boundBlur, change: boundChange },
            dispatch
          );
          return { dispatchBlur, dispatchChange };
        }, [dispatch, form, touchOnBlur, touchOnChange]);

        const connectedFormACs = bindActionCreators(boundFormACs, dispatch);
        const computedActions = {
          ...connectedFormACs,
          blur: dispatchBlur,
          change: dispatchChange,
          focus: boundFocus,
          dispatch,
        };

        useEffect(() => {
          formMounted.current = true;
          const { modtitle } = getModuleName(location);
          if (props.name && modtitle) {
            setDocumentTitle(`${props.name} - ${modtitle}`);
          }
          return () => {
            formMounted.current = false;
          };
        }, []);

        const [pristine, baseValue] = usePristineEntity({
          values: connectedFormState.values,
          entity,
          customPristineDependency,
        });

        const processFormData = useProcessFormData({
          entity,
          params,
          name: props.name,
          fields: props.formConfig?.fields,
          fieldValidation,
          initialize: connectedFormACs.initialize,
          values: connectedFormState.values,
          baseValue,
        });

        const currentFormData = props.formData?.data || props.formData?.defaultRecord;
        useEffect(() => {
          if (props.formData) {
            //Reset baseValue any time form data is re-processed, such as when we do 'Save & Add New'.
            processFormData(props.formData);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [!!currentFormData, currentFormData?.id, currentFormData?.uuid]);

        const [headerAlerts, addHeaderAlert, removeHeaderAlert] = useHeaderAlerts();
        useAlertsAndJobs(props.name, connectedFormState.values, addHeaderAlert);

        //New state value to hold additional data that will need to be saved, but is not maintained during temporary saves.
        //Must be manually set & handled by the 'submitValues' function.
        const [supportingData, setSupportingData] = useState({});

        const newform = useSelector((store) => store.newform);
        const formKeys = newform ? Object.keys(newform) : [];

        const toastProps = useP4ToastController();

        // When we're 'not' in a Modal, determine uniqueName from path/screenName as legacy support.
        // (Avoids resetting users 'tab' configurations for non-modal views).
        const { fullScreenName } = useContext(ViewContext);
        const legacyUniqueName = match?.path ?? location?.screenName ?? entity.formUrl;
        const viewContext = useMemo(
          () => ({
            uniqueName: fullScreenName ? `ConnectedForm/${entity.type}` : legacyUniqueName,
          }),
          [entity.type, fullScreenName, legacyUniqueName]
        );

        return (
          <>
            {formKeys.length > 0 && newform[formKeys[0]].conflicts && newform[formKeys[0]].conflicts.length > 0 ? (
              <MergeConflicts
                conflicts={newform[formKeys[0]].conflicts}
                form={Object.keys(newform)[0]}
                entity={entity}
                baseValue={baseValue}
              />
            ) : null}
            <ViewContextProvider value={viewContext}>
              <Form
                ref={ref}
                {...props}
                {...connectedFormState}
                {...computedActions}
                fieldValidation={fieldValidation}
                processData={processFormData}
                reloadData={reloadData}
                concurrencyReloadData={concurrencyReloadData}
                submitOptions={submitOptions}
                submitPromise={submitPromise}
                submitInProgress={submitInProgress}
                setSubmitInProgress={setSubmitInProgress}
                submitFunctions={submitFunctions}
                listenToSubmit={listenToSubmit}
                workspaceReloadRef={workspaceReloadRef}
                pristine={pristine}
                dirty={!pristine}
                modalContext={modalContext}
                navigateBack={navigateBack}
                afterSubmit={afterSubmit}
                headerAlerts={headerAlerts}
                addHeaderAlert={addHeaderAlert}
                removeHeaderAlert={removeHeaderAlert}
                supportingData={supportingData}
                setSupportingData={setSupportingData}
                baseValues={baseValue}
                toastProps={toastProps}
              />
            </ViewContextProvider>
          </>
        );
      }),
      (prev, next) => {
        //Added an override - Values wasn't being updated on DashboardForm because only an inner value changed.
        if (next.shouldComponentUpdate?.(prev, next)) return false;
        if (prev.formData?.loading && !next.formData?.loading) return false;
        if (prev.values && !prev.values.entityMetaData && next.values.entityMetaData) return false;

        return !Object.keys(next).some((prop) => {
          return !~propsToNotUpdateFor.indexOf(prop) && !deepEqual(prev[prop], next[prop]);
        });
      }
    );

    HookForm.defaultProps = config;
    return HookForm;
  };
}
